import { useEffect, useRef, useState } from "react";
import {
  AUDIO_TIME_CONSTANTS,
  POST_AUDIO_TEXTS,
} from "../constants/PostAudio.constants";
import { calculateProgressPercent } from "../utils/PostAudio.utils";
import { IUsePostAudioPlaybackReturn } from "../PostAudio.interface";

export const usePostAudioPlayback = (): IUsePostAudioPlaybackReturn => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState<number>(
    AUDIO_TIME_CONSTANTS.INITIAL_TIME,
  );
  const [duration, setDuration] = useState<number>(
    AUDIO_TIME_CONSTANTS.INITIAL_TIME,
  );

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, []);

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current
        .play()
        .then(() => {
          setIsPlaying(true);
        })
        .catch((err) => {
          setIsPlaying(false);
          console.error(POST_AUDIO_TEXTS.FAILED_TO_PLAY_ERROR, err);
        });
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(
        audioRef.current.duration || AUDIO_TIME_CONSTANTS.INITIAL_TIME,
      );
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setCurrentTime(AUDIO_TIME_CONSTANTS.INITIAL_TIME);
  };

  const handleSeek = (newTime: number) => {
    setCurrentTime(newTime);
    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
    }
  };

  const progressPercent = calculateProgressPercent(currentTime, duration);

  return {
    audioRef,
    isPlaying,
    currentTime,
    duration,
    progressPercent,
    togglePlay,
    handleTimeUpdate,
    handleLoadedMetadata,
    handleEnded,
    handleSeek,
    setIsPlaying,
  };
};
